<script setup lang="ts">
import Calendar from 'primevue/calendar'
import type { FormKitFrameworkContext } from '@formkit/core'
import dayjs from 'dayjs'

const { $formatDate } = useNuxtApp()
const calendar = ref(null)

// Watch for the Calendar component to be mounted
watch(calendar, (newCalendarInstance) => {
  // Set first day as monday once the component is available
  if (newCalendarInstance) {
    newCalendarInstance.$primevue.config.locale.firstDayOfWeek = 1
  }
})

const props = defineProps<{
  context: FormKitFrameworkContext
}>()
const selectionMode = computed<any>(() => props.context.mode || 'single')
const view = computed<any>(() => (props.context as any).view || 'date')
const dateFormat = computed(() => {
  if (view.value === 'month') return 'yy-mm'
  if (view.value === 'year') return 'yy'
  return 'yy-mm-dd'
})

const showTime = computed<any>(() => props.context.showTime || false)
const showSeconds = computed<boolean>(() =>
  props.context.attrs.props?.hideSeconds !== undefined
    ? !props.context.attrs.props.hideSeconds
    : !!showTime.value,
)

// Correctly compute disabled state from FormKit node props
const disabled = computed(() => !!props.context.node.props.disabled)

const sysTimeFormat = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
}).resolvedOptions()

const timeFormat =
  sysTimeFormat.hourCycle === 'h24' || sysTimeFormat.hourCycle === 'h23'
    ? '24'
    : '12'

const value = computed(() => {
  const d = props.context._value ?? props.context.attrs.args?.value ?? null

  // Simplified value computation - directly use the date object if available
  return !d
    ? null
    : Array.isArray(d)
    ? d.map((i) => (i ? new Date(i) : null))
    : new Date(d)
})

function handleInput(val) {
  const d = !val
    ? null
    : Array.isArray(val)
    ? val.map((i) => (i ? $formatDate(i, showTime.value) : null))
    : $formatDate(val, showTime.value)

  props.context.node.input(d)
}
</script>

<template>
  <!-- Render interactive Calendar only if not disabled -->
  <template v-if="!disabled">
    <Calendar
      ref="calendar"
      :model-value="value"
      class="h-10 w-full"
      :input-style="{
        border: '0 !important',
        'font-size': '0.875rem !important',
        color: 'rgb(55, 65, 81) !important',
      }"
      :date-format="dateFormat"
      :hour-format="timeFormat"
      :show-time="showTime"
      :disabled="disabled"
      :show-seconds="showSeconds"
      :selection-mode="selectionMode"
      :view="view"
      :placeholder="
        props.context.attrs.placeholder ||
        `rrrr-mm-dd hh:mm${showSeconds ? ':ss' : ''}`
      "
      show-button-bar
      :max-date="props.context.attrs.args?.maxDate ?? null"
      @update:model-value="handleInput"
    />
  </template>
  <!-- Render a non-interactive display when disabled -->
  <template v-else>
    <div
      class="formkit-input formkit-disabled h-10 w-full px-3 py-2 text-gray-700 border-none text-sm bg-gray-100 rounded flex items-center"
    >
      {{ value ? $formatDate(value, showTime) : '' }}
    </div>
  </template>
</template>

<style lang="postcss">
.p-calendar {
  display: flex !important;
}

.p-datepicker {
  .p-datepicker-header {
    height: 3rem !important;
  }
  .p-timepicker {
    padding: 0 !important;
    button:last-child {
      @apply -mt-1 !important;
    }
  }
  table td {
    padding: 0.2rem !important;
    span {
      width: 2rem !important;
      height: 2rem !important;
    }
  }
  .p-datepicker-buttonbar {
    padding: 0.2rem 0 !important;
  }
}

.pi-chevron-up:before {
  content: '∧';
}
.pi-chevron-down:before {
  content: '∨';
}
.pi-chevron-left:before {
  content: '<';
}
.pi-chevron-right:before {
  content: '>';
}
</style>
